import { useSearch } from "@tanstack/react-router";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";

import type { Page } from "../../api";
import { TabPage } from "../../PageFrame";
import { LandedLegend } from "../bars";
import { axisHeight, axisTicks, AxisTick, CHART, chartWidth, ReferenceLabel, ScrollChart, TOOLTIP_STYLE } from "../charts";
import type { PageProps } from "../index";
import { money, Note, PageHead } from "../kit";
import {
  byStackDesc,
  landedLines,
  latestPurchase,
  NO_PURCHASE,
  UNKNOWN_DUTY_HEIGHT,
  type LandedLine,
  type LandedRow,
  type Purchase,
} from "../landed";
import { landedExtra, pageFilter } from "../params";
import { VariantPage } from "../variant";

export interface StackPoint {
  label: string;
  raw: number;
  /** The duty, or the fixed marker height when it is unknown. */
  duty: number;
  dutyUnknown: boolean;
  transport: number;
  option: { optionId: string; itemId: string };
}

export function stackPoints(lines: LandedLine[]): StackPoint[] {
  return byStackDesc(lines).map((line) => ({
    label: line.label,
    raw: line.raw,
    duty: line.duty ?? UNKNOWN_DUTY_HEIGHT,
    dutyUnknown: line.duty === null,
    transport: line.transport,
    option: { optionId: line.row.option_id, itemId: line.row.item_id },
  }));
}

/** The top of the y axis: 12% above the tallest bar or the latest purchase. */
export function stackTop(points: StackPoint[], purchase: Purchase | null): number {
  return Math.max(0.01, purchase?.price ?? 0, ...points.map((point) => point.raw + point.duty + point.transport)) * 1.12;
}

export const HATCH_ID = "sourcing-funnel-duty-unknown";

/** Stacked bars: raw material, duty (hatched when unknown), transport, with the latest-purchase line. */
export function LandedStackChart({
  points,
  purchase,
  width,
  height,
}: {
  points: StackPoint[];
  purchase: Purchase | null;
  width?: number;
  height?: number;
}) {
  const links = points.map((point) => point.option);
  const top = stackTop(points, purchase);
  return (
    <BarChart data={points} width={width} height={height} margin={{ top: 18, right: 10, bottom: 0, left: 0 }}>
      <defs>
        <pattern id={HATCH_ID} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={6} height={6} fill={CHART.surface} />
          <line x1={0} y1={0} x2={0} y2={6} stroke={CHART.danger} strokeWidth={1.5} />
        </pattern>
      </defs>
      <CartesianGrid vertical={false} stroke={CHART.grey4} />
      <XAxis
        dataKey="label"
        interval={0}
        height={axisHeight(true, points.map((point) => point.label))}
        tickLine={false}
        stroke={CHART.grey1}
        tick={(props: { x?: number | string; y?: number | string; index?: number; payload?: { value?: string | number } }) => (
          <AxisTick {...props} rotate links={links} from="landed_cost_vs_purchase" />
        )}
      />
      <YAxis
        width={44}
        tickLine={false}
        axisLine={false}
        stroke={CHART.grey1}
        tick={{ fontSize: 10 }}
        tickFormatter={(value: number) => value.toFixed(1)}
        domain={[0, top]}
        ticks={axisTicks(top)}
      />
      <Tooltip
        cursor={false}
        contentStyle={TOOLTIP_STYLE}
        formatter={(value, name, item) =>
          name === "Duty" && (item.payload as StackPoint).dutyUnknown ? "unknown" : Number(value).toFixed(2)
        }
      />
      <Bar dataKey="raw" name="Raw" stackId="landed" fill={CHART.purple} isAnimationActive={false} />
      <Bar dataKey="duty" name="Duty" stackId="landed" fill={CHART.purple2} isAnimationActive={false}>
        {points.map((point, index) => (
          <Cell
            key={index}
            fill={point.dutyUnknown ? `url(#${HATCH_ID})` : CHART.purple2}
            stroke={point.dutyUnknown ? CHART.danger : undefined}
          />
        ))}
      </Bar>
      <Bar dataKey="transport" name="Transport" stackId="landed" fill={CHART.grey1} isAnimationActive={false} />
      {purchase ? (
        <ReferenceLine
          y={purchase.price}
          stroke={CHART.warn}
          strokeWidth={3}
          label={(props: { viewBox?: { x?: number; y?: number } }) => (
            <ReferenceLabel viewBox={props.viewBox} text={`Latest purchase ${money(purchase.price)}`} />
          )}
        />
      ) : null}
    </BarChart>
  );
}

export function LandedVsPurchaseBody({ rows, origin }: { rows: LandedRow[]; origin: string | null }) {
  const points = stackPoints(landedLines(rows, origin));
  const purchase = latestPurchase(rows[0]);
  return (
    <>
      <LandedLegend withPurchase />
      {purchase ? null : <Note>{NO_PURCHASE}</Note>}
      <ScrollChart width={chartWidth(points.length, 64)}>
        <ChartFrame title="Landed cost vs latest purchase" height={300} pointCount={points.length}>
          <LandedStackChart points={points} purchase={purchase} />
        </ChartFrame>
      </ScrollChart>
    </>
  );
}

export function LandedVsPurchasePage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const origin = pageFilter(search, "origin");
  const destination = pageFilter(search, "destination") ?? "UK";
  return (
    <VariantPage tab={tab}>
      <TabPage<Page<LandedRow>>
        tab={tab}
        areaName={areaName}
        extra={landedExtra(tab.tabKey, search)}
        isEmpty={(data) => landedLines(data.items, origin).length === 0}
        head={(data) => {
          const first = data?.items[0];
          const purchase = latestPurchase(first);
          return (
            <PageHead
              title={tab.label}
              sub={first ? `${first.variant} · landed in ${destination} vs what we last paid` : undefined}
              kpi={purchase ? { label: "Latest purchase", value: money(purchase.price) } : null}
            />
          );
        }}
      >
        {(data) => <LandedVsPurchaseBody rows={data.items} origin={origin} />}
      </TabPage>
    </VariantPage>
  );
}
